import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { FloatingActionButton } from '../../components/base/FloatingActionButton';

interface ProduitStock {
  id: number;
  nom: string;
  type: string;
  couleur: string;
  quantite_stock: number;
  unite: string;
  seuil_alerte: number;
  prix_unitaire: number;
  fournisseur: string;
  image_url: string;
  boutique_id?: number | null;
}

interface UserInfo { id: string; nom: string; role: string; boutique_id?: number | null; }

export default function GestionStocks() {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [produits, setProduits] = useState<ProduitStock[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'tous' | 'alerte' | 'ok'>('tous');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [reappModal, setReappModal] = useState<ProduitStock | null>(null);
  const [reappQty, setReappQty] = useState('10');
  const [editModal, setEditModal] = useState<ProduitStock | null>(null);
  const [editForm, setEditForm] = useState<Partial<ProduitStock> | null>(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashProduits, setTrashProduits] = useState<ProduitStock[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  
  const [activeBoutique, setActiveBoutique] = useState<number | 'toutes'>(1);
  const [transferModal, setTransferModal] = useState<ProduitStock | null>(null);
  const [transferQty, setTransferQty] = useState('');
  const [transferTargetBoutique, setTransferTargetBoutique] = useState<number>(2);

  useEffect(() => {
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored) {
      const parsedUser = JSON.parse(stored);
      setUser(parsedUser);
      if (parsedUser.role === 'vendeur') {
        setActiveBoutique(parsedUser.boutique_id || 1);
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token && user) {
      fetchStocks(token, activeBoutique === 'toutes' ? undefined : activeBoutique);
    }
  }, [activeBoutique, user]);

  const fetchStocks = (token: string, boutiqueId?: number) => {
    api.getStocks(token, boutiqueId).then(setProduits).catch((err: any) => showToast(`Erreur stocks: ${err.message || "Inconnue"}`, "error"));
  };

  const fetchTrash = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const data = await api.getTrashStocks(token, activeBoutique === 'toutes' ? undefined : activeBoutique);
      setTrashProduits(data);
      setShowTrash(true);
    } catch (err) {
      showToast("Erreur corbeille", "error");
    }
  };

  const handleTransfer = async () => {
    if (!transferModal) return;
    const qty = parseFloat(transferQty);
    if (isNaN(qty) || qty <= 0) {
      showToast("Quantité invalide", "error");
      return;
    }
    if (qty > Number(transferModal.quantite_stock)) {
      showToast("Stock insuffisant pour le transfert", "error");
      return;
    }
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
      await api.transferStock(token, {
        tissu_id: transferModal.id,
        quantite: qty,
        boutique_cible_id: transferTargetBoutique
      });
      showToast("Transfert effectué avec succès");
      fetchStocks(token, activeBoutique === 'toutes' ? undefined : activeBoutique);
      setTransferModal(null);
    } catch (err: any) {
      showToast(err.message || "Erreur de transfert", "error");
    }
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = produits.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.nom.toLowerCase().includes(q) || p.type.toLowerCase().includes(q);
    const matchFilter = filter === 'tous' || (filter === 'alerte' && Number(p.quantite_stock) <= Number(p.seuil_alerte)) || (filter === 'ok' && Number(p.quantite_stock) > Number(p.seuil_alerte));
    return matchSearch && matchFilter;
  });

  const handleReapprovisionner = async () => {
    if (!reappModal) return;
    const qty = parseFloat(reappQty);
    if (isNaN(qty) || qty <= 0) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;

    try {
        const updatedQty = Number(reappModal.quantite_stock) + qty;
        await api.updateStock(token, String(reappModal.id), { ...reappModal, quantite_stock: updatedQty });
        setProduits(prev => prev.map(p => p.id === reappModal.id ? { ...p, quantite_stock: updatedQty } : p));
        showToast(`+${qty} ${reappModal.unite} ajoutés pour ${reappModal.nom}`);
    } catch (err) {
        showToast("Erreur mise à jour stock", "error");
    } finally {
        setReappModal(null);
        setReappQty('10');
    }
  };

  const openEdit = (p: ProduitStock) => {
    setEditModal(p);
    setEditForm({ ...p });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const res = await api.uploadImage(token, file);
      if (res.url) {
        setEditForm(prev => prev ? { ...prev, image_url: res.url } : null);
        showToast("Image téléchargée");
      }
    } catch (err) {
      showToast("Erreur upload", "error");
    }
  };

  const handleSaveEdit = async () => {
    if (!editModal || !editForm) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.updateStock(token, String(editModal.id), editForm);
      setProduits(prev => prev.map(p => p.id === editModal.id ? { ...p, ...editForm } as ProduitStock : p));
      showToast(`"${editForm.nom}" mis à jour`);
    } catch (err) {
      showToast("Erreur lors de la mise à jour", "error");
    } finally {
      setEditModal(null);
      setEditForm(null);
    }
  };

  const handleDelete = async (id: number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.softDeleteStock(token, id);
      setProduits(prev => prev.filter(p => p.id !== id));
      showToast("Tissu mis à la corbeille");
    } catch (err) {
      showToast("Erreur suppression", "error");
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleRestore = async (id: number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.restoreStock(token, id);
      fetchStocks(token);
      setTrashProduits(prev => prev.filter(p => p.id !== id));
      showToast("Tissu restauré");
    } catch (err) {
      showToast("Erreur restauration", "error");
    }
  };

  const handleHardDelete = async (id: number) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.hardDeleteStock(token, id);
      setTrashProduits(prev => prev.filter(p => p.id !== id));
      showToast("Tissu supprimé définitivement");
    } catch (err) {
      showToast("Erreur suppression définitive", "error");
    }
  };

  const alerteCount = produits.filter(p => Number(p.quantite_stock) <= Number(p.seuil_alerte)).length;
  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-playfair font-bold text-xl text-gray-900">Gestion des stocks</h2>
            <p className="text-xs text-gray-400 mt-0.5">{produits.length} références · {alerteCount} alerte(s)</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchTrash}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
              title="Corbeille"
            >
              <i className="ri-delete-bin-line" />
              <span className="hidden sm:inline">Corbeille</span>
            </button>
            <button
              onClick={() => navigate('/stocks/nouveau')}
              className="hidden md:flex items-center gap-2 bg-foret-500 hover:bg-foret-600 text-white px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
              Nouveau produit
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {user.role !== 'vendeur' && (
            <div className="flex gap-2 mb-6 bg-white border border-gray-150 rounded-2xl p-1 shadow-sm max-w-md">
              <button
                onClick={() => setActiveBoutique('toutes')}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeBoutique === 'toutes' ? 'bg-foret-500 text-white shadow-md shadow-foret-100' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                Toutes
              </button>
              <button
                onClick={() => setActiveBoutique(1)}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeBoutique === 1 ? 'bg-foret-500 text-white shadow-md shadow-foret-100' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                Boutique 1
              </button>
              <button
                onClick={() => setActiveBoutique(2)}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${activeBoutique === 2 ? 'bg-foret-500 text-white shadow-md shadow-foret-100' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                Boutique 2
              </button>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1 max-w-sm">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-search-line" />
              </div>
              <input
                id="stock-search-input"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un tissu…"
                title="Rechercher un tissu par nom ou type"
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400"
              />
            </div>
            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
              {([
                { id: 'tous', label: 'Tous' },
                { id: 'alerte', label: `⚠ Alertes (${alerteCount})` },
                { id: 'ok', label: '✓ OK' },
              ] as const).map(f => (
                <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${filter === f.id ? 'bg-foret-500 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Mobile Swipe-like Cards */}
            <div className="md:hidden divide-y divide-gray-50 pb-20">
              {filtered.map(p => {
                const isAlerte = Number(p.quantite_stock) <= Number(p.seuil_alerte);
                return (
                  <div key={p.id} className={`p-4 ${isAlerte ? 'bg-red-50/30' : 'bg-white'}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative">
                        <img src={api.getProxyUrl(p.image_url)} alt={p.nom} className="w-16 h-16 rounded-xl object-cover object-top border border-gray-100 shadow-sm" />
                        {isAlerte && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center border-2 border-white animate-pulse">
                            <i className="ri-error-warning-fill text-[10px]" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-gray-900 truncate uppercase mt-0.5">{p.nom}</p>
                        <p className="text-xs text-gray-400 font-bold">{p.type} · {p.couleur}</p>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                           <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${isAlerte ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                             {isAlerte ? '⚠ ALERTE STOCK' : '✓ EN STOCK'}
                           </span>
                           <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                             Boutique {p.boutique_id || 1}
                           </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-base font-black ${isAlerte ? 'text-red-600' : 'text-gray-900'}`}>
                          {p.quantite_stock}
                        </p>
                        <p className="text-[10px] text-gray-400 font-black uppercase">{p.unite}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => openEdit(p)}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-400 border border-gray-100 active:bg-gray-100 transition-colors shrink-0"
                        title="Modifier le produit"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => { setReappModal(p); setReappQty('10'); }}
                        className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-foret-100 active:scale-95 transition-all text-center"
                      >
                        <i className="ri-add-line mr-1" /> Recharger
                      </button>
                      {user.role !== 'vendeur' && (
                        <button
                          onClick={() => { setTransferModal(p); setTransferQty(''); setTransferTargetBoutique(p.boutique_id === 2 ? 1 : 2); }}
                          className="px-3 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-[10px] font-black uppercase border border-blue-100"
                        >
                          Transférer
                        </button>
                      )}
                      {deleteConfirmId === p.id ? (
                        <div className="flex gap-1 animate-in slide-in-from-right-5">
                          <button onClick={() => handleDelete(p.id)} className="px-3 py-2.5 bg-red-600 text-white rounded-xl text-[10px] font-black uppercase">OUI</button>
                          <button onClick={() => setDeleteConfirmId(null)} className="px-3 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-[10px] font-black uppercase">NON</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(p.id)}
                          className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500 border border-red-50 active:bg-red-100 shrink-0"
                          title="Supprimer le produit"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Produit</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Boutique</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Stock</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">Statut</th>
                    <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map(p => {
                    const isAlerte = Number(p.quantite_stock) <= Number(p.seuil_alerte);
                    return (
                      <tr key={p.id} className={`hover:bg-gray-50/80 transition-colors ${isAlerte ? 'bg-red-50/40' : ''}`}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <img src={api.getProxyUrl(p.image_url)} alt={p.nom} className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{p.nom}</p>
                              <p className="text-xs text-gray-400">{p.type} · {p.couleur}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-bold text-gray-700 bg-gray-100 px-2.5 py-1 rounded-full">
                            Boutique {p.boutique_id || 1}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-bold text-sm ${isAlerte ? 'text-red-600' : 'text-gray-800'}`}>
                            {p.quantite_stock} {p.unite}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${isAlerte ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {isAlerte ? '⚠ Alerte' : '✓ OK'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEdit(p)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 cursor-pointer transition-colors"
                              title="Modifier"
                            >
                              <i className="ri-edit-line" />
                            </button>
                            <button
                              onClick={() => { setReappModal(p); setReappQty('10'); }}
                              className="text-xs bg-foret-500 hover:bg-foret-600 text-white px-3 py-1.5 rounded-lg cursor-pointer transition-colors font-semibold whitespace-nowrap"
                            >
                              +Stock
                            </button>
                            {user.role !== 'vendeur' && (
                              <button
                                onClick={() => { setTransferModal(p); setTransferQty(''); setTransferTargetBoutique(p.boutique_id === 2 ? 1 : 2); }}
                                className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg cursor-pointer transition-colors font-semibold whitespace-nowrap"
                              >
                                Transférer
                              </button>
                            )}
                            {deleteConfirmId === p.id ? (
                              <div className="flex gap-1 animate-in fade-in slide-in-from-right-2 duration-300">
                                <button onClick={() => handleDelete(p.id)} className="text-[10px] bg-red-500 text-white px-2 py-1.5 rounded-lg font-bold cursor-pointer">OUI</button>
                                <button onClick={() => setDeleteConfirmId(null)} className="text-[10px] bg-gray-200 text-gray-600 px-2 py-1.5 rounded-lg font-bold cursor-pointer">NON</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirmId(p.id)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 cursor-pointer transition-colors"
                                title="Supprimer"
                              >
                                <i className="ri-delete-bin-line" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {reappModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setReappModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-1">Réapprovisionner</h3>
            <p className="text-sm text-gray-500 mb-5">{reappModal.nom} · Stock actuel : <strong>{reappModal.quantite_stock} {reappModal.unite}</strong></p>
            <div className="flex gap-2 mb-4">
              {['5', '10', '20', '50'].map(qty => (
                <button key={qty} onClick={() => setReappQty(qty)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border cursor-pointer transition-colors whitespace-nowrap ${reappQty === qty ? 'bg-foret-500 text-white border-foret-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  +{qty}
                </button>
              ))}
            </div>
            <input type="number" value={reappQty} onChange={e => setReappQty(e.target.value)} min="1"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 mb-5" placeholder="Quantité personnalisée" />
            <div className="flex gap-3">
              <button onClick={() => setReappModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">Annuler</button>
              <button onClick={handleReapprovisionner} className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white font-semibold text-sm cursor-pointer hover:bg-foret-600 whitespace-nowrap">Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {editModal && editForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-playfair font-bold text-lg text-gray-900">Modifier le tissu</h3>
              <button onClick={() => setEditModal(null)} title="Fermer" className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-500">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              {user.role !== 'vendeur' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Boutique de stockage</label>
                  <select
                    value={editForm.boutique_id || 1}
                    onChange={e => setEditForm(prev => prev ? { ...prev, boutique_id: Number(e.target.value) } : null)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 cursor-pointer"
                  >
                    <option value={1}>Boutique 1</option>
                    <option value={2}>Boutique 2</option>
                  </select>
                </div>
              )}
              {[
                { label: 'Nom du produit', key: 'nom', type: 'text' },
                { label: 'Type', key: 'type', type: 'text' },
                { label: 'Couleur', key: 'couleur', type: 'text' },
                { label: 'Unité', key: 'unite', type: 'text' },
                { label: 'Seuil d\'alerte', key: 'seuil_alerte', type: 'number' },
                { label: 'Prix unitaire (FCFA)', key: 'prix_unitaire', type: 'number' },
                { label: 'Fournisseur', key: 'fournisseur', type: 'text' },
              ].map(field => (
                <div key={field.key}>
                  <label htmlFor={`edit-stock-${field.key}`} className="block text-xs font-semibold text-gray-700 mb-1.5">{field.label}</label>
                  <input
                    id={`edit-stock-${field.key}`}
                    type={field.type}
                    value={String((editForm as any)[field.key] ?? '')}
                    onChange={e => setEditForm(prev => prev ? {
                      ...prev,
                      [field.key]: field.type === 'number' ? Number(e.target.value) : e.target.value,
                    } : null)}
                    placeholder={field.label}
                    title={field.label}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50"
                  />
                </div>
              ))}
               <div>
                 <label className="block text-xs font-semibold text-gray-700 mb-1.5">Image du produit</label>
                 <div className="flex flex-col gap-3">
                   <div className="flex items-center gap-4">
                     {editForm.image_url ? (
                       <img src={api.getProxyUrl(editForm.image_url)} alt="Aperçu" className="w-20 h-20 rounded-xl object-cover border-2 border-foret-100 shadow-sm" />
                     ) : (
                       <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 border-2 border-dashed border-gray-200">
                         <i className="ri-image-line text-2xl" />
                       </div>
                     )}
                     <div className="flex-1">
                       <label className="flex flex-col items-center justify-center h-12 px-4 py-2 bg-white text-foret-600 rounded-xl border border-foret-200 cursor-pointer hover:bg-foret-50 transition-colors text-sm font-semibold">
                         <div className="flex items-center gap-2">
                           <i className="ri-upload-cloud-2-line" />
                           Modifier l'image
                         </div>
                         <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
                       </label>
                     </div>
                   </div>
                 </div>
               </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">Annuler</button>
              <button onClick={handleSaveEdit} className="flex-1 py-2.5 rounded-xl bg-foret-500 text-white font-semibold text-sm cursor-pointer hover:bg-foret-600 whitespace-nowrap">Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {showTrash && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowTrash(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[80vh] flex flex-col p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-playfair font-bold text-2xl text-gray-900">Corbeille Tissus</h3>
                <p className="text-sm text-gray-500">{trashProduits.length} références supprimées</p>
              </div>
              <button onClick={() => setShowTrash(false)} title="Fermer" className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 cursor-pointer transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {trashProduits.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                  <i className="ri-delete-bin-line text-6xl mb-4 opacity-20" />
                  <p className="text-lg font-medium">La corbeille est vide</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {trashProduits.map(p => (
                    <div key={p.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-100 flex items-center gap-4 group">
                      <img src={api.getProxyUrl(p.image_url)} alt={p.nom} className="w-16 h-16 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">{p.nom}</p>
                        <p className="text-xs text-gray-500">{p.type} · {p.couleur}</p>
                        <p className="text-xs font-semibold text-gray-400 mt-1">{p.quantite_stock} {p.unite} restants</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleRestore(p.id)}
                          className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        >
                          <i className="ri-refresh-line" /> Restaurer
                        </button>
                        <button
                          onClick={() => { if(confirm('Supprimer définitivement ce tissu ?')) handleHardDelete(p.id); }}
                          className="flex items-center justify-center gap-2 bg-white hover:bg-red-50 text-red-500 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line" /> Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end">
              <button 
                onClick={() => setShowTrash(false)}
                className="px-6 py-2.5 rounded-xl bg-gray-900 text-white font-bold text-sm hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {transferModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setTransferModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-playfair font-bold text-lg text-gray-900 mb-1">Transférer du stock</h3>
            <p className="text-sm text-gray-500 mb-5">{transferModal.nom} (Boutique {transferModal.boutique_id || 1}) · Disponible : <strong>{transferModal.quantite_stock} {transferModal.unite}</strong></p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Boutique de destination</label>
                <select
                  value={transferTargetBoutique}
                  onChange={e => setTransferTargetBoutique(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 cursor-pointer"
                >
                  {transferModal.boutique_id === 2 ? (
                    <option value={1}>Boutique 1</option>
                  ) : (
                    <option value={2}>Boutique 2</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Quantité à transférer ({transferModal.unite})</label>
                <input
                  type="number"
                  value={transferQty}
                  onChange={e => setTransferQty(e.target.value)}
                  max={transferModal.quantite_stock}
                  min="0.1"
                  step="any"
                  placeholder="Ex: 5"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-foret-400"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button onClick={() => setTransferModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer hover:bg-gray-50">Annuler</button>
              <button onClick={handleTransfer} className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm cursor-pointer hover:bg-blue-700">Transférer</button>
            </div>
          </div>
        </div>
      )}

      <FloatingActionButton 
        onClick={() => navigate('/stocks/nouveau')} 
        icon="ri-add-line" 
        label="Nouveau produit" 
      />
    </div>
  );
}
